import { useEffect, useRef, useCallback } from 'react';
import { createStyles } from '@mantine/core';

const GHOST_CHAR_ATTRIBUTE = 'data-ghost-char';
const GHOST_CHAR_CLASS = 'ghost-char';

const useMultiplierStyles = createStyles((theme) => ({
  contentEditable: {
    fontSize: 14,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    color: theme.black,
    outline: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    minHeight: 24,
    '&[contenteditable="false"]': {
      color: theme.colors.gray[6],
      cursor: 'not-allowed',
    },
    [`& .${GHOST_CHAR_CLASS}`]: {
      marginLeft: /* eslint-disable-line local-rules/use-theme-tokens */ 0.5,
      color: theme.black,
      transition: 'color 0.15s ease',
      userSelect: 'none',
      pointerEvents: 'none',
    },
    '&:focus': {
      [`& .${GHOST_CHAR_CLASS}`]: {
        color: theme.colors.gray[6],
      },
    },
  },
}));

const insertGhostCharacter = (element: HTMLDivElement): void => {
  const ghostElements = element.querySelectorAll(`[${GHOST_CHAR_ATTRIBUTE}]`);
  ghostElements.forEach((el) => el.remove());

  const ghostChar = document.createElement('span');
  ghostChar.className = GHOST_CHAR_CLASS;
  ghostChar.setAttribute(GHOST_CHAR_ATTRIBUTE, 'true');
  ghostChar.contentEditable = 'false';
  ghostChar.textContent = 'x';
  element.appendChild(ghostChar);
};

export interface MultiplierTextInputProps {
  defaultValue?: number;
  onChange: (value: number | undefined) => void;
  isDisabled?: boolean;
  className?: string;
  testId?: string;
}

export function MultiplierTextInput({
  defaultValue,
  onChange,
  isDisabled = false,
  className,
  testId,
}: MultiplierTextInputProps) {
  const { classes, cx } = useMultiplierStyles();
  const inputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.textContent = defaultValue?.toString() || '';
    insertGhostCharacter(inputRef.current);
  }, [defaultValue]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    if (!inputRef.current) return;
    insertGhostCharacter(inputRef.current);
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const number = parseFloat(text);
    if (Number.isNaN(number)) return;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(number.toFixed(2));
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);

    if (inputRef.current) {
      const inputEvent = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(inputEvent);
    }
  }, []);

  const preventCursorAfterX = useCallback(() => {
    if (!inputRef.current) return;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const ghostCharElement = inputRef.current.querySelector(`[${GHOST_CHAR_ATTRIBUTE}]`);
    if (!ghostCharElement) return;

    const range = selection.getRangeAt(0);
    const currentNode = range.startContainer;

    if (
      currentNode === inputRef.current &&
      range.startOffset >= inputRef.current.childNodes.length
    ) {
      const textNode = inputRef.current.childNodes[0];
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const textLength = textNode.textContent?.length ?? 0;
        const newRange = document.createRange();
        newRange.setStart(textNode, textLength);
        newRange.setEnd(textNode, textLength);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  }, []);

  const handleBlur = useCallback(() => {
    if (!inputRef.current) return;

    setTimeout(() => {
      if (!inputRef.current) return;

      const textContent = inputRef.current.textContent.replace('x', '');
      const number = parseFloat(textContent);
      if (Number.isNaN(number)) return;
      onChange(number);

      inputRef.current.textContent = number.toString();
      insertGhostCharacter(inputRef.current);
    }, 0);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Allow navigation keys and shortcuts
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Delete'];
    const isNavigationKey = allowedKeys.includes(e.key);
    const isShortcut =
      (e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());

    // Prevent navigation past the multiplier character
    if (isNavigationKey || isShortcut) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setTimeout(preventCursorAfterX, 0);
      }
      return;
    }

    // Only allow numbers and decimal points
    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    if (inputRef.current) {
      const textContent = inputRef.current.textContent.replace('x', '');
      const tokens = textContent.split('.');

      if (tokens.length > 1) {
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          const range = selection.getRangeAt(0);
          const cursorPosition = range.startOffset;
          const decimalIndex = textContent.indexOf('.');

          // Prevent more than 2 decimal places
          if (cursorPosition > decimalIndex && tokens[1].length >= 2) {
            e.preventDefault();
            return;
          }
        }
      }

      // Prevent multiple decimal points
      if (e.key === '.' && textContent.includes('.')) {
        e.preventDefault();
      }
    }
  }, []);

  const handleSelect = useCallback(() => {
    preventCursorAfterX();
  }, []);

  return (
    <div
      ref={inputRef}
      role="textbox"
      aria-label="Multiplier value"
      aria-disabled={isDisabled}
      tabIndex={0}
      contentEditable={!isDisabled}
      className={className ? cx(classes.contentEditable, className) : classes.contentEditable}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onSelect={handleSelect}
      data-test-id={testId}
      suppressContentEditableWarning
    />
  );
}
